import {
  ArrowPathIcon,
  ArrowsUpDownIcon,
  EllipsisVerticalIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Checkbox,
  Group,
  Loader,
  Menu,
  Modal,
  NumberInput,
  Pagination,
  Paper,
  rem,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useCallback, useEffect, useMemo, useState } from "react";

// Suppress ResizeObserver error (common React development error)
const resizeObserverErrorHandler = (error: ErrorEvent) => {
  if (
    error.message &&
    error.message.includes(
      "ResizeObserver loop completed with undelivered notifications"
    )
  ) {
    error.preventDefault();
    return true;
  }
  return false;
};

if (typeof window !== "undefined") {
  window.addEventListener("error", resizeObserverErrorHandler);
}

export interface DictionaryItem {
  id: number;
  title?: string;
  name?: string;
  [key: string]: any;
}

export interface DictionaryService<T extends DictionaryItem> {
  getDictionary(): Promise<T[]>;
  createItem(item: Omit<T, "id">): Promise<T>;
  updateItem(id: number, item: Partial<T>): Promise<T>;
  deleteItem(id: number): Promise<void>;
  consolidateItems?(sourceId: number, targetId: number): Promise<T>;
}

interface DictionaryManagementProps<T extends DictionaryItem> {
  title: string;
  service: DictionaryService<T>;
  itemDisplayName: string;
  itemDisplayNamePlural: string;
  canConsolidate?: boolean;
  titleFieldName?: string;
  searchPlaceholder?: string;
  showReviewStatus?: boolean; // New prop to show review status column
  showNormalizedText?: boolean; // New prop to show normalized text column
  normalizedFieldName?: string; // Field name for normalized text
  onApproveItem?: (item: T) => Promise<void>; // Callback for approving items
  onRejectItem?: (item: T) => Promise<void>; // Callback for rejecting items
}

const ITEMS_PER_PAGE = 50; // Pagination size

export function DictionaryManagement<T extends DictionaryItem>({
  title,
  service,
  itemDisplayName,
  itemDisplayNamePlural,
  canConsolidate = false,
  titleFieldName = "title",
  searchPlaceholder = "Search...",
  showReviewStatus = false,
  showNormalizedText = false,
  normalizedFieldName = "normalizedName",
  onApproveItem,
  onRejectItem,
}: DictionaryManagementProps<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [filteredItems, setFilteredItems] = useState<T[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [deletingItem, setDeletingItem] = useState<T | null>(null);
  const [consolidatingItems, setConsolidatingItems] = useState<{
    source: T;
    target: T;
  } | null>(null);
  const [newItemTitle, setNewItemTitle] = useState("");
  const [editItemTitle, setEditItemTitle] = useState("");
  const [selectedForConsolidation, setSelectedForConsolidation] = useState<T[]>(
    []
  );
  const [sourceItem, setSourceItem] = useState<string>("");
  const [targetItem, setTargetItem] = useState<string>("");

  const [
    createModalOpened,
    { open: openCreateModal, close: closeCreateModal },
  ] = useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [
    consolidateModalOpened,
    { open: openConsolidateModal, close: closeConsolidateModal },
  ] = useDisclosure(false);

  const [operationLoading, setOperationLoading] = useState(false);
  const [reviewingIds, setReviewingIds] = useState<Set<number>>(new Set());

  // Load data with better error handling
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await service.getDictionary();
      setItems(data);
    } catch (err) {
      console.error(`Error loading ${itemDisplayNamePlural}:`, err);
      setError(
        err instanceof Error
          ? err.message
          : `Failed to load ${itemDisplayNamePlural}`
      );
    } finally {
      setLoading(false);
    }
  }, [service, itemDisplayNamePlural]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter items based on debounced search query
  useEffect(() => {
    if (!debouncedSearchQuery.trim()) {
      setFilteredItems(items);
      setCurrentPage(1);
    } else {
      const filtered = items.filter((item) =>
        item[titleFieldName]
          ?.toLowerCase()
          .includes(debouncedSearchQuery.toLowerCase())
      );
      setFilteredItems(filtered);
      setCurrentPage(1);
    }
  }, [debouncedSearchQuery, items, titleFieldName]);

  // Paginated items for current page
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  // Handle create item
  const handleCreateItem = useCallback(async () => {
    if (!newItemTitle.trim()) return;

    try {
      setOperationLoading(true);
      const newItem = await service.createItem({
        [titleFieldName]: newItemTitle.trim(),
      } as Omit<T, "id">);

      notifications.show({
        title: "Success",
        message: `${itemDisplayName} created successfully`,
        color: "green",
      });

      setNewItemTitle("");
      closeCreateModal();
      await loadData();
    } catch (err) {
      console.error(`Error creating ${itemDisplayName}:`, err);
      notifications.show({
        title: "Error",
        message: `Failed to create ${itemDisplayName}`,
        color: "red",
      });
    } finally {
      setOperationLoading(false);
    }
  }, [
    newItemTitle,
    service,
    titleFieldName,
    itemDisplayName,
    closeCreateModal,
    loadData,
  ]);

  // Handle edit item
  const handleEditItem = useCallback(async () => {
    if (!editingItem || !editItemTitle.trim()) return;

    try {
      setOperationLoading(true);
      await service.updateItem(editingItem.id, {
        [titleFieldName]: editItemTitle.trim(),
      } as Partial<T>);

      notifications.show({
        title: "Success",
        message: `${itemDisplayName} updated successfully`,
        color: "green",
      });

      setEditingItem(null);
      setEditItemTitle("");
      closeEditModal();
      await loadData();
    } catch (err) {
      console.error(`Error updating ${itemDisplayName}:`, err);
      notifications.show({
        title: "Error",
        message: `Failed to update ${itemDisplayName}`,
        color: "red",
      });
    } finally {
      setOperationLoading(false);
    }
  }, [
    editingItem,
    editItemTitle,
    service,
    titleFieldName,
    itemDisplayName,
    closeEditModal,
    loadData,
  ]);

  // Handle delete item
  const handleDeleteItem = useCallback(async () => {
    if (!deletingItem) return;

    try {
      setOperationLoading(true);
      await service.deleteItem(deletingItem.id);

      notifications.show({
        title: "Success",
        message: `${itemDisplayName} deleted successfully`,
        color: "green",
      });

      setDeletingItem(null);
      closeDeleteModal();
      await loadData();
    } catch (err) {
      console.error(`Error deleting ${itemDisplayName}:`, err);
      notifications.show({
        title: "Error",
        message: `Failed to delete ${itemDisplayName}`,
        color: "red",
      });
    } finally {
      setOperationLoading(false);
    }
  }, [deletingItem, service, itemDisplayName, closeDeleteModal, loadData]);

  // Handle checkbox selection for consolidation
  const handleConsolidationSelect = useCallback(
    (item: T, checked: boolean) => {
      if (checked) {
        if (selectedForConsolidation.length < 2) {
          setSelectedForConsolidation((prev) => [...prev, item]);
        }
      } else {
        setSelectedForConsolidation((prev) =>
          prev.filter((selected) => selected.id !== item.id)
        );
      }
    },
    [selectedForConsolidation.length]
  );

  // Open consolidate modal with selected items
  const handleOpenConsolidateModal = useCallback(() => {
    if (selectedForConsolidation.length === 2) {
      setSourceItem(selectedForConsolidation[0].id.toString());
      setTargetItem(selectedForConsolidation[1].id.toString());
      openConsolidateModal();
    }
  }, [selectedForConsolidation, openConsolidateModal]);

  // Swap source and target
  const handleSwapItems = useCallback(() => {
    const temp = sourceItem;
    setSourceItem(targetItem);
    setTargetItem(temp);
  }, [sourceItem, targetItem]);

  // Handle consolidate items
  const handleConsolidateItems = useCallback(async () => {
    if (
      !sourceItem ||
      !targetItem ||
      sourceItem === targetItem ||
      !service.consolidateItems
    )
      return;

    try {
      setOperationLoading(true);
      await service.consolidateItems(
        parseInt(sourceItem),
        parseInt(targetItem)
      );

      notifications.show({
        title: "Success",
        message: `${itemDisplayNamePlural} consolidated successfully`,
        color: "green",
      });

      setSourceItem("");
      setTargetItem("");
      setSelectedForConsolidation([]);
      closeConsolidateModal();
      await loadData();
    } catch (err) {
      console.error(`Error consolidating ${itemDisplayNamePlural}:`, err);
      notifications.show({
        title: "Error",
        message: `Failed to consolidate ${itemDisplayNamePlural}`,
        color: "red",
      });
    } finally {
      setOperationLoading(false);
    }
  }, [
    sourceItem,
    targetItem,
    service,
    itemDisplayNamePlural,
    closeConsolidateModal,
    loadData,
  ]);

  // Open edit modal
  const openEditModalForItem = useCallback(
    (item: T) => {
      setEditingItem(item);
      setEditItemTitle(item[titleFieldName]);
      openEditModal();
    },
    [titleFieldName, openEditModal]
  );

  // Open delete modal
  const openDeleteModalForItem = useCallback(
    (item: T) => {
      setDeletingItem(item);
      openDeleteModal();
    },
    [openDeleteModal]
  );

  // Get selected items for display (in correct order based on swap)
  const getSelectedItemsForDisplay = useCallback(() => {
    if (selectedForConsolidation.length !== 2)
      return { source: null, target: null };

    const sourceIndex = selectedForConsolidation.findIndex(
      (item) => item.id.toString() === sourceItem
    );
    const targetIndex = selectedForConsolidation.findIndex(
      (item) => item.id.toString() === targetItem
    );

    return {
      source:
        sourceIndex !== -1
          ? selectedForConsolidation[sourceIndex]
          : selectedForConsolidation[0],
      target:
        targetIndex !== -1
          ? selectedForConsolidation[targetIndex]
          : selectedForConsolidation[1],
    };
  }, [selectedForConsolidation, sourceItem, targetItem]);

  // Approve item handler
  const handleApproveItem = useCallback(
    async (item: T) => {
      if (!onApproveItem) return;

      setReviewingIds((prev) => new Set(prev).add(item.id));
      try {
        await onApproveItem(item);

        // Update the item in the local state to reflect the change
        setItems((prev) =>
          prev.map((i) =>
            i.id === item.id ? ({ ...i, requiresAdminReview: false } as T) : i
          )
        );

        notifications.show({
          title: "Approved",
          message: `${itemDisplayName} "${item[titleFieldName]}" has been approved`,
          color: "green",
        });
      } catch (error) {
        console.error(`Error approving ${itemDisplayName}:`, error);
        notifications.show({
          title: "Error",
          message: `Failed to approve ${itemDisplayName}`,
          color: "red",
        });
      } finally {
        setReviewingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(item.id);
          return newSet;
        });
      }
    },
    [onApproveItem, itemDisplayName, titleFieldName]
  );

  // Reject item handler
  const handleRejectItem = useCallback(
    async (item: T) => {
      if (!onRejectItem) return;

      setReviewingIds((prev) => new Set(prev).add(item.id));
      try {
        await onRejectItem(item);

        // Remove the item from the local state
        setItems((prev) => prev.filter((i) => i.id !== item.id));

        notifications.show({
          title: "Rejected",
          message: `${itemDisplayName} "${item[titleFieldName]}" has been rejected and removed`,
          color: "orange",
        });
      } catch (error) {
        console.error(`Error rejecting ${itemDisplayName}:`, error);
        notifications.show({
          title: "Error",
          message: `Failed to reject ${itemDisplayName}`,
          color: "red",
        });
      } finally {
        setReviewingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(item.id);
          return newSet;
        });
      }
    },
    [onRejectItem, itemDisplayName, titleFieldName]
  );

  if (loading) {
    return (
      <Center py="xl">
        <Stack align="center">
          <Loader size="lg" />
          <Text>Loading {itemDisplayNamePlural.toLowerCase()}...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error">
        {error}
        <Button onClick={loadData} mt="md" variant="outline" size="sm">
          Try Again
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={2} size="h3" fw={600}>
            {title}
          </Title>
          <Text size="sm" c="dimmed">
            Manage {itemDisplayNamePlural.toLowerCase()} in the system
          </Text>
        </div>
        <Group>
          {canConsolidate && (
            <Button
              leftSection={<LinkIcon style={{ width: rem(16) }} />}
              onClick={handleOpenConsolidateModal}
              variant="outline"
              color="blue"
              disabled={selectedForConsolidation.length !== 2}
            >
              Consolidate ({selectedForConsolidation.length}/2)
            </Button>
          )}
          <Button
            leftSection={<PlusIcon style={{ width: rem(16) }} />}
            onClick={openCreateModal}
          >
            Add {itemDisplayName}
          </Button>
        </Group>
      </Group>

      {/* Search and Actions */}
      <Group justify="space-between" align="flex-end">
        <TextInput
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          leftSection={<MagnifyingGlassIcon style={{ width: rem(18) }} />}
          rightSection={
            searchQuery ? (
              <ActionIcon
                onClick={() => setSearchQuery("")}
                variant="subtle"
                size="sm"
              >
                <XMarkIcon style={{ width: rem(16) }} />
              </ActionIcon>
            ) : null
          }
          style={{ flex: 1, maxWidth: 400 }}
        />
        <Group align="flex-end">
          <NumberInput
            label="Items per page"
            value={itemsPerPage}
            onChange={(value) => {
              const numValue =
                typeof value === "string" ? parseInt(value) : value;
              setItemsPerPage(numValue || ITEMS_PER_PAGE);
              setCurrentPage(1);
            }}
            min={10}
            max={200}
            step={10}
            style={{ width: 150 }}
          />
          <Button
            leftSection={<ArrowPathIcon style={{ width: rem(16) }} />}
            onClick={loadData}
            variant="default"
            loading={loading}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      {/* Statistics */}
      <Group>
        <Badge size="lg" variant="outline">
          Total: {items.length}
        </Badge>
        {searchQuery && (
          <Badge size="lg" variant="outline" color="blue">
            Filtered: {filteredItems.length}
          </Badge>
        )}
        <Badge size="lg" variant="outline" color="gray">
          Page {currentPage} of {totalPages}
        </Badge>
      </Group>

      {/* Table */}
      <Paper withBorder>
        <ScrollArea>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>{itemDisplayName}</Table.Th>
                {showNormalizedText && <Table.Th>Normalized</Table.Th>}
                {showReviewStatus && <Table.Th>Status</Table.Th>}
                <Table.Th style={{ width: "120px" }}>Actions</Table.Th>
                {canConsolidate && (
                  <Table.Th style={{ width: "100px" }}>Consolidate</Table.Th>
                )}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td
                    colSpan={
                      canConsolidate
                        ? showReviewStatus
                          ? showNormalizedText
                            ? 6
                            : 5
                          : showNormalizedText
                          ? 5
                          : 4
                        : showReviewStatus
                        ? showNormalizedText
                          ? 5
                          : 4
                        : showNormalizedText
                        ? 4
                        : 3
                    }
                  >
                    <Center py="xl">
                      <Loader size="sm" />
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : paginatedItems.length === 0 ? (
                <Table.Tr>
                  <Table.Td
                    colSpan={
                      canConsolidate
                        ? showReviewStatus
                          ? showNormalizedText
                            ? 6
                            : 5
                          : showNormalizedText
                          ? 5
                          : 4
                        : showReviewStatus
                        ? showNormalizedText
                          ? 5
                          : 4
                        : showNormalizedText
                        ? 4
                        : 3
                    }
                  >
                    <Center py="xl">
                      <Text c="dimmed">
                        {searchQuery
                          ? "No items match your search"
                          : `No ${itemDisplayNamePlural.toLowerCase()} found`}
                      </Text>
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : (
                paginatedItems.map((item) => (
                  <Table.Tr key={item.id}>
                    <Table.Td>
                      <Text>{item.id}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fw={500}>{item[titleFieldName]}</Text>
                    </Table.Td>
                    {showNormalizedText && (
                      <Table.Td>
                        <Text
                          size="sm"
                          c="dimmed"
                          style={{ fontFamily: "monospace" }}
                        >
                          {(item as any)[normalizedFieldName] || "N/A"}
                        </Text>
                      </Table.Td>
                    )}
                    {showReviewStatus && (
                      <Table.Td>
                        {(item as any).requiresAdminReview ? (
                          <Badge color="orange" variant="light" size="sm">
                            Needs Review
                          </Badge>
                        ) : (
                          <Badge color="green" variant="light" size="sm">
                            Approved
                          </Badge>
                        )}
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Group gap="xs">
                        {/* Standard edit/delete actions (most frequently used) */}
                        <Tooltip
                          key={`edit-${item.id}`}
                          label={`Edit ${itemDisplayName}`}
                        >
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="blue"
                            onClick={() => openEditModalForItem(item)}
                          >
                            <PencilIcon style={{ width: rem(16) }} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip
                          key={`delete-${item.id}`}
                          label={`Delete ${itemDisplayName}`}
                        >
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="red"
                            onClick={() => openDeleteModalForItem(item)}
                          >
                            <TrashIcon style={{ width: rem(16) }} />
                          </ActionIcon>
                        </Tooltip>
                        {/* Review actions dropdown (used only once per item) */}
                        {onApproveItem &&
                          onRejectItem &&
                          (item as any).requiresAdminReview && (
                            <Menu shadow="md" width={120}>
                              <Menu.Target>
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  color="gray"
                                  loading={reviewingIds.has(item.id)}
                                  disabled={reviewingIds.has(item.id)}
                                >
                                  <EllipsisVerticalIcon
                                    style={{ width: rem(16) }}
                                  />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  color="green"
                                  onClick={() => handleApproveItem(item)}
                                  disabled={reviewingIds.has(item.id)}
                                >
                                  Approve
                                </Menu.Item>
                                <Menu.Item
                                  color="red"
                                  onClick={() => handleRejectItem(item)}
                                  disabled={reviewingIds.has(item.id)}
                                >
                                  Reject
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          )}
                      </Group>
                    </Table.Td>
                    {canConsolidate && (
                      <Table.Td>
                        <Checkbox
                          checked={selectedForConsolidation.some(
                            (selected) => selected.id === item.id
                          )}
                          onChange={(event) =>
                            handleConsolidationSelect(
                              item,
                              event.currentTarget.checked
                            )
                          }
                          disabled={
                            !selectedForConsolidation.some(
                              (selected) => selected.id === item.id
                            ) && selectedForConsolidation.length >= 2
                          }
                        />
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="center">
          <Pagination
            total={totalPages}
            value={currentPage}
            onChange={setCurrentPage}
            size="sm"
          />
        </Group>
      )}

      {/* Create Modal */}
      <Modal
        opened={createModalOpened}
        onClose={closeCreateModal}
        title={`Add New ${itemDisplayName}`}
      >
        <Stack>
          <TextInput
            label={`${itemDisplayName} Name`}
            placeholder={`Enter ${itemDisplayName.toLowerCase()} name`}
            value={newItemTitle}
            onChange={(event) => setNewItemTitle(event.currentTarget.value)}
            required
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeCreateModal}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateItem}
              loading={operationLoading}
              disabled={!newItemTitle.trim()}
            >
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Modal */}
      <Modal
        opened={editModalOpened}
        onClose={closeEditModal}
        title={`Edit ${itemDisplayName}`}
      >
        <Stack>
          <TextInput
            label={`${itemDisplayName} Name`}
            placeholder={`Enter ${itemDisplayName.toLowerCase()} name`}
            value={editItemTitle}
            onChange={(event) => setEditItemTitle(event.currentTarget.value)}
            required
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button
              onClick={handleEditItem}
              loading={operationLoading}
              disabled={!editItemTitle.trim()}
            >
              Update
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title={`Delete ${itemDisplayName}`}
      >
        <Stack>
          <Group>
            <ExclamationTriangleIcon
              style={{
                width: rem(20),
                height: rem(20),
                color: "var(--mantine-color-red-6)",
              }}
            />
            <Text>
              Are you sure you want to delete this{" "}
              {itemDisplayName.toLowerCase()}?
            </Text>
          </Group>
          {deletingItem && (
            <Alert color="red" variant="light">
              <Text fw={500}>"{deletingItem[titleFieldName]}"</Text>
            </Alert>
          )}
          <Text size="sm" c="dimmed">
            This action cannot be undone.
          </Text>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeleteItem}
              loading={operationLoading}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Consolidate Modal */}
      {canConsolidate && (
        <Modal
          opened={consolidateModalOpened}
          onClose={closeConsolidateModal}
          title={`Consolidate ${itemDisplayNamePlural}`}
          size="xl"
          style={{
            maxWidth: "90vw",
            width: "800px",
          }}
        >
          <Stack>
            <Text size="sm" c="dimmed">
              Consolidate two {itemDisplayNamePlural.toLowerCase()} by moving
              all references from the source to the target.
            </Text>

            {/* Source Item */}
            <Stack gap="xs">
              <Text fw={500} size="sm">
                Source (will be removed)
              </Text>
              <Paper withBorder p="md" bg="red.0">
                <Group justify="space-between">
                  <div>
                    <Text size="sm" c="dimmed">
                      ID: {getSelectedItemsForDisplay().source?.id}
                    </Text>
                    <Text fw={500}>
                      {getSelectedItemsForDisplay().source?.[titleFieldName]}
                    </Text>
                  </div>
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={
                      <ArrowsUpDownIcon style={{ width: rem(14) }} />
                    }
                    onClick={handleSwapItems}
                  >
                    Swap
                  </Button>
                </Group>
              </Paper>
            </Stack>

            {/* Target Item */}
            <Stack gap="xs">
              <Text fw={500} size="sm">
                Target (will remain)
              </Text>
              <Paper withBorder p="md" bg="green.0">
                <div>
                  <Text size="sm" c="dimmed">
                    ID: {getSelectedItemsForDisplay().target?.id}
                  </Text>
                  <Text fw={500}>
                    {getSelectedItemsForDisplay().target?.[titleFieldName]}
                  </Text>
                </div>
              </Paper>
            </Stack>

            <Alert color="yellow" variant="light">
              <Text size="sm">
                All references to the source {itemDisplayName.toLowerCase()}{" "}
                will be moved to the target {itemDisplayName.toLowerCase()}, and
                the source will be deleted. This action cannot be undone.
              </Text>
            </Alert>
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={closeConsolidateModal}>
                Cancel
              </Button>
              <Button
                color="orange"
                onClick={handleConsolidateItems}
                loading={operationLoading}
                disabled={
                  !sourceItem || !targetItem || sourceItem === targetItem
                }
              >
                Consolidate
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </Stack>
  );
}
